// src/components/Login.js
import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import sessionStore from '../stores/sessionStore'
import i18nStore from '../stores/i18nStore'
import { useLocation, useNavigate } from 'react-router-dom'
import {
	TextField,
	Button,
	Typography,
	Link,
	Paper,
	Container,
	Alert,
	CircularProgress,
	Box,
	Divider
} from '@mui/material'
import { FaWhatsapp } from "react-icons/fa"
import { RiTelegram2Line } from "react-icons/ri"
import logger from '../utils/logger'
import { authErrorMessage, toAuthError } from '../auth/authErrors'

function Login() {
	const navigate = useNavigate()
	const location = useLocation()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [needsVerification, setNeedsVerification] = useState(false)
	const [resendSent, setResendSent] = useState(false)
	const authChecked = sessionStore.authStatus !== 'initializing'

	logger.log('LOGIN компонент загружен')

	const handleSubmit = async e => {
		e.preventDefault()
		setError('')
		setNeedsVerification(false)
		setLoading(true)

		try {
			await sessionStore.loginByEmail(email, password)
			const target = location.state?.from || sessionStorage.getItem('reldo:returnTo') || '/requests'
			sessionStorage.removeItem('reldo:returnTo')
			navigate(target, { replace: true })
		} catch (err) {
			const authError = toAuthError(err)
			setNeedsVerification(authError.code === 'EMAIL_NOT_VERIFIED')
			setError(authErrorMessage(authError))
		} finally {
			setLoading(false)
		}
	}

	const handleResend = async () => {
		setLoading(true)
		try {
			await sessionStore.resendVerification(email)
			setResendSent(true)
		} catch (err) {
			setError(authErrorMessage(err))
		} finally {
			setLoading(false)
		}
	}

	if (!authChecked || loading) {
		return (
			<Container component='main' maxWidth='xs' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
				<div style={{ marginLeft: '10px' }}>
					{!authChecked ? 'Проверка авторизации...' : 'Вход в систему...'}
				</div>
			</Container>
		)
	}

	// Если пользователь уже авторизован, сразу перенаправляем
	if (sessionStore.user) {
		const target = location.state?.from || sessionStorage.getItem('reldo:returnTo') || '/requests'
		navigate(target, { replace: true });
		return null;
	}

	return (
		<Container component='main' maxWidth='xs'>
		
			<Paper elevation={3} style={{ padding: 20, marginTop: 40 }}>
			<Typography variant='h6' style={{ marginBottom: 20 }} sx={{color: 'text.primary'}}>
						Reldo - система управления недвижимостью.
					</Typography>
					<Typography variant="body1" style={{ marginBottom: 20, textAlign: 'center' }}>
          Вход в систему
        </Typography>
				{error && (
					<Alert severity='error' style={{ marginBottom: 20 }}>
						{error}
					</Alert>
				)}
				{resendSent && <Alert severity='success'>Если аккаунт существует, письмо отправлено.</Alert>}
				<form onSubmit={handleSubmit}>
					<TextField
						variant='outlined'
						margin='normal'
						required
						fullWidth
						label={i18nStore.translations?.login?.email || 'Электронная почта'}
						autoComplete='email'
						autoFocus
						value={email}
						size='small'
						onChange={e => setEmail(e.target.value)}
					/>
					<TextField
						variant='outlined'
						margin='normal'
						required
						fullWidth
						label={i18nStore.translations?.login?.password || 'Пароль'}
						type='password'
						autoComplete='current-password'
						value={password}
						size='small'
						onChange={e => setPassword(e.target.value)}
					/>
					<Button
						type='submit'
						fullWidth
						variant='contained'
						color='primary'
						style={{ margin: '24px 0 16px' }}
						disabled={loading}
					>
						Войти
					</Button>
				</form>
				{needsVerification && <Button fullWidth onClick={handleResend} disabled={loading}>Отправить письмо повторно</Button>}
				<Typography>
					<Link href='/forgot' variant='body2'>
						Забыли пароль?
					</Link>
				</Typography>
				<Typography style={{ marginBottom: 20 }}>
					Нет учетной записи?{' '}
					<Link href='/register' variant='body2'>
						Зарегистрироваться
					</Link>
				</Typography>
				
				<Divider sx={{ my: 2 }} />
				
				<Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
					Если вам требуется помощь, пишите:
				</Typography>
				<Box>
					<Link 
						href="https://wa.me/79882602218" 
						target="_blank" 
						rel="noopener noreferrer"
						sx={{ 
							textDecoration: 'none', 
							color: '#25D366', 
							fontWeight: 'bold',
							display: 'flex',
							alignItems: 'center',
							gap: '5px',
							mb: 1
						}}
					>
						<FaWhatsapp style={{ fontSize: '18px' }} /> +7988-260-22-18
					</Link>
					<Link 
						href="https://t.me/reldo_crm" 
						target="_blank" 
						rel="noopener noreferrer"
						sx={{ 
							textDecoration: 'none', 
							color: '#0088cc', 
							fontWeight: 'bold',
							display: 'flex',
							alignItems: 'center',
							gap: '5px' 
						}}
					>
						<RiTelegram2Line style={{ fontSize: '18px' }} /> @reldo_crm
					</Link>
				</Box>
			</Paper>
		</Container>
	)
}

export default observer(Login)
